/**
 * Reorder a visible subset of repeatable units and reassign global displayOrder
 * (#999 Story 3.7 / 3.9 — Devices view shared order).
 *
 * Used when Devices shows a location-filtered table: drag indices apply to the
 * visible list only; units outside the filter keep their relative positions.
 *
 * Cross-status moves are rejected (Devices STATUS-flat tables stay
 * EXISTING-then-NEW like step section reorder / FR47).
 */

import { coerceDevicesUnitId } from './devicesEditorNavigation';
import {
  normalizeRepeatableUnitStatus,
  type RepeatableUnitStatusBucket,
} from './groupUnitsByLocationThenStatus';

export type OrderableUnit = {
  id?: number | string | null;
  displayOrder?: number | null;
  status?: string | null;
  rackStatus?: string | null;
};

export function getOrderableUnitStatusBucket(
  unit: OrderableUnit,
): RepeatableUnitStatusBucket {
  const raw =
    typeof unit.rackStatus === 'string' && unit.rackStatus.length > 0
      ? unit.rackStatus
      : typeof unit.status === 'string'
      ? unit.status
      : undefined;
  return normalizeRepeatableUnitStatus(raw);
}

function sortByDisplayOrder<T extends OrderableUnit>(units: readonly T[]): T[] {
  return [...units].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
  );
}

function visibleIdsAreUnique(
  visibleUnitsInOrder: readonly OrderableUnit[],
): boolean {
  const ids: number[] = [];
  for (const unit of visibleUnitsInOrder) {
    const id = coerceDevicesUnitId(unit.id);
    if (id == null) {
      return false;
    }
    ids.push(id);
  }
  return new Set(ids).size === ids.length;
}

/**
 * @param allUnits - Full collection for the equipment type (request-scoped).
 * @param visibleUnitsInOrder - Units as currently shown (e.g. Hall A racks).
 * @param fromIndex - Index within visibleUnitsInOrder.
 * @param toIndex - Index within visibleUnitsInOrder.
 */
export function applyVisibleUnitsReorder<T extends OrderableUnit>(
  allUnits: readonly T[],
  visibleUnitsInOrder: readonly T[],
  fromIndex: number,
  toIndex: number,
): T[] {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    visibleUnitsInOrder.length < 2 ||
    fromIndex >= visibleUnitsInOrder.length ||
    toIndex >= visibleUnitsInOrder.length
  ) {
    return [...allUnits];
  }

  if (!visibleIdsAreUnique(visibleUnitsInOrder)) {
    return [...allUnits];
  }

  const fromUnit = visibleUnitsInOrder[fromIndex];
  const toUnit = visibleUnitsInOrder[toIndex];
  if (!fromUnit || !toUnit) {
    return [...allUnits];
  }

  const fromBucket = getOrderableUnitStatusBucket(fromUnit);
  const toBucket = getOrderableUnitStatusBucket(toUnit);
  // Devices flattens EXISTING+NEW; reject cross-status so DO stays aligned with
  // step section reorder (FR47).
  if (fromBucket !== toBucket) {
    return [...allUnits];
  }

  const sameStatusVisible = visibleUnitsInOrder.filter(
    unit => getOrderableUnitStatusBucket(unit) === fromBucket,
  );
  const fromId = coerceDevicesUnitId(fromUnit.id);
  const toId = coerceDevicesUnitId(toUnit.id);
  const fromRel = sameStatusVisible.findIndex(
    unit => coerceDevicesUnitId(unit.id) === fromId,
  );
  const toRel = sameStatusVisible.findIndex(
    unit => coerceDevicesUnitId(unit.id) === toId,
  );
  if (fromRel < 0 || toRel < 0 || fromRel === toRel) {
    return [...allUnits];
  }

  const reorderedVisible = [...sameStatusVisible];
  const [moved] = reorderedVisible.splice(fromRel, 1);
  if (!moved) {
    return [...allUnits];
  }
  reorderedVisible.splice(toRel, 0, moved);

  const visibleIdSet = new Set<number>();
  for (const unit of sameStatusVisible) {
    const id = coerceDevicesUnitId(unit.id);
    if (id != null) {
      visibleIdSet.add(id);
    }
  }

  let visibleCursor = 0;
  const sortedAll = sortByDisplayOrder(allUnits);
  const merged = sortedAll.map(unit => {
    const id = coerceDevicesUnitId(unit.id);
    if (id != null && visibleIdSet.has(id)) {
      const next = reorderedVisible[visibleCursor];
      visibleCursor += 1;
      return next ?? unit;
    }
    return unit;
  });

  return merged.map((unit, index) => ({
    ...unit,
    displayOrder: index + 1,
  }));
}
